import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Typography, Tag, Card, Space, Row, Col, Modal } from 'antd';
import {
  CheckCircleFilled, ClockCircleOutlined, RocketOutlined,
  BulbOutlined, ApiOutlined, BookOutlined, TeamOutlined,
  RobotOutlined, ThunderboltOutlined, NodeIndexOutlined,
  LeftOutlined, RightOutlined, WarningOutlined,
  FullscreenOutlined, FullscreenExitOutlined, FileMarkdownOutlined,
} from '@ant-design/icons';
import { BusinessCaseContent } from './BusinessCasePage';

const { Content } = Layout;
const { Title, Text } = Typography;

// ── Slide 1: Qué es Cubicup Brain ────────────────────────────────────────────

interface ConceptStartup { name: string; desc: string }
interface ConceptCard {
  label: string;
  subtitle: string;
  ref: string;
  tagColor: string;
  intro: string;
  startups: ConceptStartup[];
}

const CONCEPT_CARDS: ConceptCard[] = [
  {
    label: 'Context Platform',
    subtitle: 'La capa de datos',
    ref: 'Glean · Dust.tt · Cassidy',
    tagColor: 'blue',
    intro: 'Herramientas que conectan todas tus aplicaciones (Slack, Gmail, Drive) para que la IA sepa qué está pasando en tu empresa en tiempo real.',
    startups: [
      { name: 'Glean', desc: 'El estándar de oro en búsqueda y contexto empresarial con IA.' },
      { name: 'Dust.tt', desc: 'Muy potente para crear "asistentes con memoria" de equipo.' },
      { name: 'Cassidy AI', desc: 'Conecta tus herramientas y crea flujos de trabajo que "entienden" tu negocio.' },
    ],
  },
  {
    label: 'Agent as a Service',
    subtitle: 'Agentes especializados',
    ref: 'Harvey AI · Cognition · Sierra',
    tagColor: 'purple',
    intro: 'En lugar de una IA generalista, son startups que venden "empleados digitales" expertos en un solo nicho.',
    startups: [
      { name: 'Harvey AI', desc: 'El agente líder para el sector legal — revisa contratos y jurisprudencia.' },
      { name: 'Cognition (Devin)', desc: 'El primer "ingeniero de software" totalmente autónomo.' },
      { name: 'Sierra', desc: 'La startup de agentes de atención al cliente de alta gama (fundada por el ex-CTO de Salesforce).' },
    ],
  },
  {
    label: 'Cubicup OS',
    subtitle: 'El sistema operativo',
    ref: 'Copilot Studio · LangChain',
    tagColor: 'cyan',
    intro: 'El "pegamento" que hace que todo lo anterior funcione unido — la capa de orquestación central sobre la que corren todos los agentes y flujos.',
    startups: [
      { name: 'Microsoft Copilot Studio', desc: 'El intento de Microsoft de ser el "cerebro" central de las empresas.' },
      { name: 'LangChain', desc: 'El marco técnico que la mayoría de startups usan por debajo para que los agentes "hablen" entre sí.' },
    ],
  },
  {
    label: 'Knowledge Graph',
    subtitle: 'Grafos de pensamiento',
    ref: 'Tana · Heptabase · Mem AI',
    tagColor: 'green',
    intro: 'Como Obsidian, estas startups se centran en cómo organizar la información de forma no lineal para que la IA la "entienda" mejor.',
    startups: [
      { name: 'Tana', desc: 'Probablemente la herramienta más avanzada ahora mismo en gestión de datos estructurados y grafos.' },
      { name: 'Heptabase', desc: 'Una pizarra visual que conecta notas y conceptos — ideal para investigación compleja.' },
      { name: 'Mem.ai', desc: 'Un bloc de notas que se organiza solo mediante IA, creando conexiones automáticas.' },
    ],
  },
  {
    label: 'Agent Builder',
    subtitle: 'Constructores sin código',
    ref: 'Relevance AI · Flowise · n8n',
    tagColor: 'orange',
    intro: 'Plataformas para que cualquier persona pueda "fabricar" sus propios agentes uniendo piezas — como si fuera un Lego.',
    startups: [
      { name: 'Relevance AI', desc: 'La plataforma de moda para crear "plantillas de trabajadores" de IA.' },
      { name: 'Flowise / Langflow', desc: 'Herramientas de código abierto para arrastrar y soltar componentes de IA.' },
      { name: 'n8n / Make', desc: 'Los clásicos de la automatización que ahora han integrado "nodos de IA" para orquestar agentes.' },
    ],
  },
  {
    label: 'Persistent Context',
    subtitle: 'Memoria y Rutinas',
    ref: 'Anthropic Projects · OpenAI GPTs',
    tagColor: 'volcano',
    intro: 'Herramientas que permiten que la IA tenga una "personalidad" constante y ejecute tareas repetitivas sin que tengas que pedírselo cada vez.',
    startups: [
      { name: 'Anthropic Projects (Claude)', desc: 'Permite crear espacios de trabajo con documentos fijos y reglas de comportamiento.' },
      { name: 'OpenAI GPTs', desc: 'Versiones personalizadas de ChatGPT con instrucciones y archivos permanentes.' },
    ],
  },
];

const Slide1: React.FC = () => {
  const [selected, setSelected] = useState<ConceptCard | null>(null);

  return (
  <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 52px', gap: 18 }}>

    {/* Header */}
    <div style={{ textAlign: 'center', flexShrink: 0 }}>
      <Space size={8} style={{ marginBottom: 10 }}>
        <Tag color="blue" style={{ fontWeight: 700, fontSize: 11, padding: '2px 10px', letterSpacing: 0.5 }}>CONTEXT PLATFORM</Tag>
        <Tag color="purple" style={{ fontWeight: 700, fontSize: 11, padding: '2px 10px', letterSpacing: 0.5 }}>AGENT AS A SERVICE</Tag>
        <Tag color="cyan" style={{ fontWeight: 700, fontSize: 11, padding: '2px 10px', letterSpacing: 0.5 }}>CUBICUP OS</Tag>
      </Space>
      <Title level={2} style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 800 }}>¿Qué es Cubicup Brain?</Title>
      <Text style={{ fontSize: 14, color: '#595959', maxWidth: 700, display: 'block', lineHeight: 1.55 }}>
        Un <strong>Expediente Digital</strong> y una <strong>Memoria Organizacional</strong> — alimentada por los empleados,
        vitaminada por IA, y consumible tanto por humanos como por máquinas.
      </Text>
    </div>

    {/* Flow diagram */}
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, width: '100%', maxWidth: 900, flexShrink: 0 }}>

      {/* Input: Empleados */}
      <div style={{ flex: 1, borderRadius: '12px 0 0 12px', background: '#f0f7ff', border: '1px solid #91caff', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 7, background: '#1677ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TeamOutlined style={{ color: '#fff', fontSize: 14 }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 11, color: '#1677ff', display: 'block', letterSpacing: 0.3 }}>ALIMENTADA POR</Text>
            <Text strong style={{ fontSize: 14 }}>Los Empleados</Text>
          </div>
        </div>
        {/* Tech badge */}
        <div style={{ marginBottom: 10 }}>
          <Tag icon={<FileMarkdownOutlined />} style={{ fontSize: 10, background: '#e6f4ff', border: '1px solid #91caff', color: '#1677ff' }}>
            Obsidian · Markdown
          </Tag>
        </div>
        {['Documentos y procedimientos', 'Reuniones y acuerdos', 'Perfiles de clientes y leads', 'Presupuestos y proyectos'].map((t, i) => (
          <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginBottom: 5 }}>
            <Text style={{ color: '#1677ff', fontSize: 12, lineHeight: 1 }}>›</Text>
            <Text style={{ fontSize: 11.5, color: '#262626', lineHeight: 1.45 }}>{t}</Text>
          </div>
        ))}
      </div>

      {/* Arrow */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', flexShrink: 0 }}>
        <div style={{ width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: '9px solid #91caff' }} />
      </div>

      {/* Brain hub */}
      <div style={{
        width: 190, flexShrink: 0, borderRadius: 12,
        background: 'linear-gradient(160deg, #1e3a8a 0%, #2563eb 60%, #3b82f6 100%)',
        padding: '16px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        boxShadow: '0 4px 20px rgba(37,99,235,0.3)',
      }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BookOutlined style={{ color: '#fff', fontSize: 20 }} />
        </div>
        <Text style={{ color: '#fff', fontWeight: 800, fontSize: 16, textAlign: 'center', lineHeight: 1.2 }}>Cubicup Brain</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
          {[
            { icon: <NodeIndexOutlined />, label: 'Expediente Digital' },
            { icon: <RobotOutlined />, label: 'Vitaminado por IA' },
          ].map((item, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 6, padding: '5px 9px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>{item.icon}</span>
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 10.5 }}>{item.label}</Text>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', flexShrink: 0 }}>
        <div style={{ width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: '9px solid #d3adf7' }} />
      </div>

      {/* Output: Humanos + Máquinas */}
      <div style={{ flex: 1, borderRadius: '0 12px 12px 0', background: '#f9f0ff', border: '1px solid #d3adf7', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 7, background: '#722ed1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ApiOutlined style={{ color: '#fff', fontSize: 14 }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 11, color: '#722ed1', display: 'block', letterSpacing: 0.3 }}>CONSUMIBLE POR</Text>
            <Text strong style={{ fontSize: 14 }}>Humanos y Máquinas</Text>
          </div>
        </div>
        {/* Comparison badge */}
        <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Tag style={{ fontSize: 10, background: '#f5f5f5', border: '1px solid #d9d9d9', color: '#8c8c8c' }}>Obsidian → solo Humanos</Tag>
          <Tag style={{ fontSize: 10, background: '#f9f0ff', border: '1px solid #d3adf7', color: '#722ed1', fontWeight: 600 }}>Brain → Humanos + Máquinas</Tag>
        </div>
        {['Equipo Cubicup (editor web)', 'Agentes IA vía RAG', 'Claude Desktop vía MCP', 'APIs y webhooks externos'].map((t, i) => (
          <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginBottom: 5 }}>
            <Text style={{ color: '#722ed1', fontSize: 12, lineHeight: 1 }}>›</Text>
            <Text style={{ fontSize: 11.5, color: '#262626', lineHeight: 1.45 }}>{t}</Text>
          </div>
        ))}
      </div>
    </div>

    {/* Tagline */}
    <Card
      style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: 10, width: '100%', maxWidth: 900, flexShrink: 0 }}
      styles={{ body: { padding: '10px 22px', textAlign: 'center' } }}
    >
      <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
        "El knowledge de Cubicup, disponible para cualquier agente, en cualquier momento, sin trabajo manual adicional."
      </Text>
    </Card>

    {/* Concept cards */}
    <div style={{ width: '100%', maxWidth: 900, flexShrink: 0 }}>
      <Text style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 10 }}>
        En el universo de startups que levantan millones · Cubicup Brain es todo esto
      </Text>
      <Row gutter={[10, 10]}>
        {CONCEPT_CARDS.map((c, i) => (
          <Col key={i} xs={12} sm={8} md={4}>
            <div
              onClick={() => setSelected(c)}
              style={{
                borderRadius: 8, border: '1px solid #f0f0f0', background: '#fafafa',
                padding: '10px 12px', height: '100%', cursor: 'pointer',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#1677ff'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(22,119,255,0.12)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#f0f0f0'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                <Text strong style={{ fontSize: 12, color: '#262626' }}>{c.label}</Text>
                <Tag color={c.tagColor} style={{ fontSize: 9, padding: '0 5px', margin: 0, lineHeight: '16px' }}>ver</Tag>
              </div>
              <Text style={{ fontSize: 10, color: '#1677ff', display: 'block', marginBottom: 4 }}>{c.ref}</Text>
              <Text style={{ fontSize: 10.5, color: '#595959', lineHeight: 1.45 }}>{c.intro.substring(0, 70)}…</Text>
            </div>
          </Col>
        ))}
      </Row>
    </div>

    {/* Detail modal */}
    <Modal
      open={!!selected}
      onCancel={() => setSelected(null)}
      footer={null}
      width={560}
      title={
        selected && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tag color={selected.tagColor} style={{ fontWeight: 700, fontSize: 11, margin: 0 }}>{selected.label}</Tag>
            <Text style={{ fontSize: 15, fontWeight: 700, color: '#141414' }}>{selected.subtitle}</Text>
          </div>
        )
      }
    >
      {selected && (
        <div style={{ paddingTop: 4 }}>
          <div style={{ background: '#f8f9ff', borderRadius: 8, padding: '12px 16px', marginBottom: 20, borderLeft: '3px solid #1677ff' }}>
            <Text style={{ fontSize: 13, color: '#262626', lineHeight: 1.65 }}>{selected.intro}</Text>
          </div>
          <Text style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 12 }}>
            Referentes del sector
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selected.startups.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: '#f0f0f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  fontSize: 12, fontWeight: 800, color: '#595959',
                }}>
                  {s.name.charAt(0)}
                </div>
                <div>
                  <Text strong style={{ display: 'block', fontSize: 13, marginBottom: 2 }}>{s.name}</Text>
                  <Text style={{ fontSize: 12, color: '#595959', lineHeight: 1.55 }}>{s.desc}</Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>

  </div>
  );
};

// ── Slide 2: Obsidian vs Cubicup Brain ───────────────────────────────────────

const obsidianPros = [
  'Ecosistema masivo — miles de plugins maduros',
  'Integraciones con Google Drive, GitHub y servicios populares (via plugins)',
  'Puede aproximarse a un Grafo de Conocimiento con plugins como Dataview',
  'Funciona offline, 100% local',
  'Markdown nativo — sin lock-in, portabilidad total',
  'Gratuito para uso personal',
];
const obsidianCons = [
  'Al ser local, cada integración debe instalarse en cada ordenador',
  'Sin servicio enterprise nativo — roles y permisos muy limitados',
  'Dependencia de plugins de terceros — riesgo de roturas en actualizaciones',
  'Los agentes de IA no pueden escribir en él de forma autónoma',
  'Sin API nativa para que sistemas externos consulten el conocimiento',
];
const brainPros = [
  'Zero-install — funciona en el browser, sin setup por máquina',
  'Multiusuario nativo con roles (admin/editor/viewer)',
  'Los agentes leen el Brain como contexto (RAG)',
  'Los agentes escriben al Brain automáticamente',
  'Historial de versiones y registro de auditoría de cada cambio (quién, qué y cuándo)',
  'Integraciones nativas con GitHub, Monday.com y BigQuery',
  'Control total sobre el dato y la seguridad — conecta con la API propia o cualquier MCP',
  'Equipo de ingeniería y producto propio que conoce en profundidad el stack y el negocio de Cubicup',
];
const brainCons = [
  'Todo debe desarrollarse internamente — sin ecosistema de plugins',
  'Requiere conexión a internet',
  'Producto más nuevo — ecosistema y comunidad limitados',
  'No apto para uso personal individual',
];

const Slide2: React.FC = () => {
  const [graphModal, setGraphModal] = useState<'obsidian' | 'neo4j' | null>(null);
  return (
  <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 48px', gap: 18 }}>

    {/* Headline */}
    <div style={{ width: '100%', maxWidth: 920, textAlign: 'center' }}>
      <Text style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 12 }}>
        Obsidian vs Cubicup Brain
      </Text>
      <div style={{
        background: 'linear-gradient(135deg, #f0f7ff 0%, #f9f0ff 100%)',
        border: '1px solid #d3adf7',
        borderRadius: 12,
        padding: '16px 28px',
      }}>
        <Text style={{ fontSize: 15.5, color: '#141414', lineHeight: 1.65, fontStyle: 'italic' }}>
          "Para entender la diferencia entre Obsidian y Cubicup Brain, lo más sencillo es ver a{' '}
          <strong style={{ color: '#6c3fbf', fontStyle: 'normal' }}>Obsidian como una herramienta para humanos</strong>
          {' '}y a{' '}
          <strong style={{ color: '#1677ff', fontStyle: 'normal' }}>Cubicup Brain como una infraestructura para humanos e IAs trabajando juntos.</strong>"
        </Text>
      </div>
    </div>

    {/* Info card rows — shared token styles */}
    {(() => {
      const obsidian = { bg: '#f9f0ff', border: '1px solid #efdbff', iconBg: '#6c3fbf', titleColor: '#22075e', accentColor: '#6c3fbf' };
      const brain    = { bg: '#e6f4ff', border: '1px solid #91caff', iconBg: '#1677ff', titleColor: '#003a8c', accentColor: '#1677ff' };

      const IconBox = ({ theme, icon }: { theme: typeof obsidian; icon: React.ReactNode }) => (
        <div style={{ width: 28, height: 28, borderRadius: 7, background: theme.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </div>
      );

      const InfoCard = ({ theme, title, badge, children }: { theme: typeof obsidian; title: React.ReactNode; badge: React.ReactNode; children: React.ReactNode }) => (
        <Card
          style={{ flex: 1, borderRadius: 10, border: theme.border, background: theme.bg }}
          styles={{ body: { padding: '14px 18px', display: 'flex', flexDirection: 'column', height: '100%' } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {title}
            {badge}
          </div>
          <div style={{ flex: 1 }}>{children}</div>
        </Card>
      );

      return (
        <>
          {/* Row 1 — definitions */}
          <Row gutter={16} style={{ width: '100%', maxWidth: 920, flexShrink: 0 }}>
            <Col xs={24} md={12} style={{ display: 'flex' }}>
              <InfoCard
                theme={obsidian}
                title={<><IconBox theme={obsidian} icon={<BulbOutlined style={{ color: '#fff', fontSize: 13 }} />} /><Text strong style={{ fontSize: 13, color: obsidian.titleColor }}>¿Qué es Obsidian?</Text></>}
                badge={<Tag style={{ fontSize: 10, background: '#f9f0ff', border: '1px solid #efdbff', color: obsidian.accentColor }}>Herramienta · Humanos</Tag>}
              >
                <Text style={{ fontSize: 12, color: '#595959', lineHeight: 1.65 }}>
                  Obsidian es un editor de notas en Markdown que vive en tu ordenador. Su superpoder es el{' '}
                  <strong>grafo de conocimiento</strong>: puedes enlazar documentos entre sí y ver cómo se relacionan visualmente.
                  Potente, flexible y offline — pero diseñado para que lo lea y escriba <strong>un humano</strong>.
                </Text>
              </InfoCard>
            </Col>
            <Col xs={24} md={12} style={{ display: 'flex' }}>
              <InfoCard
                theme={brain}
                title={<><IconBox theme={brain} icon={<NodeIndexOutlined style={{ color: '#fff', fontSize: 13 }} />} /><Text strong style={{ fontSize: 13, color: brain.titleColor }}>¿Qué es un Grafo de Conocimiento?</Text></>}
                badge={<Tag style={{ fontSize: 10, background: '#e6f4ff', border: '1px solid #91caff', color: brain.accentColor }}>Infraestructura · IAs + Humanos</Tag>}
              >
                <Text style={{ fontSize: 12, color: '#595959', lineHeight: 1.65 }}>
                  Una red donde los nodos son conceptos (personas, proyectos, ideas) y las aristas son sus relaciones.
                  En lugar de carpetas lineales, el conocimiento se organiza como <strong>una red de conexiones</strong> —
                  igual que funciona el cerebro humano, y exactamente cómo necesita consumirlo una IA para razonar sobre tu negocio.
                </Text>
              </InfoCard>
            </Col>
          </Row>

          {/* Row 2 — graph usage */}
          <Row gutter={16} style={{ width: '100%', maxWidth: 920, flexShrink: 0 }}>
            <Col xs={24} md={12} style={{ display: 'flex' }}>
              <InfoCard
                theme={obsidian}
                title={<><IconBox theme={obsidian} icon={<BulbOutlined style={{ color: '#fff', fontSize: 13 }} />} /><Text strong style={{ fontSize: 12, color: obsidian.titleColor }}>En Obsidian: el grafo es para</Text></>}
                badge={<Tag style={{ fontSize: 10, background: '#f9f0ff', border: '1px solid #efdbff', color: obsidian.accentColor }}>Vistas · Humanos</Tag>}
              >
                <Text style={{ fontSize: 11.5, color: '#595959', lineHeight: 1.65 }}>
                  El grafo es una <strong>capa visual</strong> que ayuda al usuario a descubrir cómo se conectan sus pensamientos —{' '}
                  <em>"Ah, mira, mi nota de Filosofía se conecta con esta de Programación"</em>.
                  El grafo reside en la interfaz de usuario; no existe fuera de ella ni puede ser consultado por una IA.
                </Text>
              </InfoCard>
            </Col>
            <Col xs={24} md={12} style={{ display: 'flex' }}>
              <InfoCard
                theme={brain}
                title={<><IconBox theme={brain} icon={<NodeIndexOutlined style={{ color: '#fff', fontSize: 13 }} />} /><Text strong style={{ fontSize: 12, color: brain.titleColor }}>En Cubicup Brain: el grafo es para</Text></>}
                badge={<Tag style={{ fontSize: 10, background: '#e6f4ff', border: '1px solid #91caff', color: brain.accentColor }}>Razonamiento · IAs</Tag>}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { label: 'GraphRAG', desc: 'La IA navega jerarquías y dependencias — "¿qué documentos cambian si modifico este precio?"' },
                    { label: 'Fuente de verdad única', desc: 'Agentes de Ventas, Técnico y Marketing beben del mismo grafo — sin alucinaciones.' },
                    { label: 'Memoria autónoma', desc: 'Los agentes escriben y actualizan el grafo solos. La memoria crece sin intervención humana.' },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                      <Text style={{ color: brain.accentColor, fontSize: 12, lineHeight: 1, marginTop: 2 }}>›</Text>
                      <Text style={{ fontSize: 11.5, color: '#262626', lineHeight: 1.5 }}>
                        <strong>{item.label}:</strong> {item.desc}
                      </Text>
                    </div>
                  ))}
                </div>
              </InfoCard>
            </Col>
          </Row>

          {/* Row 3 — images as cards */}
          <Row gutter={16} style={{ width: '100%', maxWidth: 920, flexShrink: 0 }}>
            <Col xs={24} md={12} style={{ display: 'flex' }}>
              <Card
                onClick={() => setGraphModal('obsidian')}
                style={{ flex: 1, borderRadius: 10, border: '1px solid #efdbff', background: obsidian.bg, overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                styles={{ body: { padding: 0 } }}
                onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(108,63,191,0.18)'}
                onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'}
              >
                <img
                  src="https://forum.obsidian.md/uploads/default/optimized/1X/a7fefb722b92c129d277510f83623169ceaac83b_2_690x459.jpeg"
                  alt="Obsidian graph view"
                  style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 160 }}
                />
                <div style={{ padding: '8px 14px' }}>
                  <Text style={{ fontSize: 11, color: obsidian.accentColor, fontWeight: 600 }}>Obsidian Graph View</Text>
                  <Text style={{ fontSize: 10, color: '#8c8c8c', display: 'block' }}>Visualización para humanos · <span style={{ color: obsidian.accentColor }}>ver más →</span></Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12} style={{ display: 'flex' }}>
              <Card
                onClick={() => setGraphModal('neo4j')}
                style={{ flex: 1, borderRadius: 10, border: '1px solid #91caff', background: brain.bg, overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                styles={{ body: { padding: 0 } }}
                onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(22,119,255,0.18)'}
                onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'}
              >
                <img
                  src="https://dist.neo4j.com/wp-content/uploads/20230322152841/1wuLzLiIuJV-pyZUnccuNjA.png"
                  alt="Knowledge graph — Neo4j"
                  style={{ width: '100%', display: 'block', objectFit: 'contain', maxHeight: 160, background: '#fff' }}
                />
                <div style={{ padding: '8px 14px' }}>
                  <Text style={{ fontSize: 11, color: brain.accentColor, fontWeight: 600 }}>Neo4j Knowledge Graph</Text>
                  <Text style={{ fontSize: 10, color: '#8c8c8c', display: 'block' }}>Infraestructura para IAs · <span style={{ color: brain.accentColor }}>ver más →</span></Text>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      );
    })()}

    {/* Comparison table */}
    {(() => {
      const rows = [
        { concept: '¿Para quién es?',  obsidian: 'Para el ojo humano.',          brain: 'Para el algoritmo de la IA.' },
        { concept: 'La Conexión',       obsidian: 'Es "muda" (solo un link).',    brain: 'Tiene Significado (tipo de relación).' },
        { concept: 'Metadatos',         obsidian: 'Muy limitados.',               brain: 'Infinitos (propiedades en nodos y flechas).' },
        { concept: 'Capacidad',         obsidian: 'Explorar ideas.',              brain: 'Calcular respuestas.' },
      ];
      const th: React.CSSProperties = { padding: '8px 16px', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', borderBottom: '2px solid #f0f0f0' };
      const td: React.CSSProperties = { padding: '9px 16px', fontSize: 12.5, lineHeight: 1.5, borderBottom: '1px solid #f5f5f5' };
      return (
        <div style={{ width: '100%', maxWidth: 920, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #f0f0f0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ ...th, color: '#8c8c8c', width: '22%', textAlign: 'left' }}>Concepto</th>
                <th style={{ ...th, color: '#6c3fbf', width: '39%', textAlign: 'left' }}>Grafo de Obsidian</th>
                <th style={{ ...th, color: '#1677ff', width: '39%', textAlign: 'left' }}>Grafo de Datos (Neo4j / Cubicup Brain)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ ...td, fontWeight: 600, color: '#262626' }}>{row.concept}</td>
                  <td style={{ ...td, color: '#6c3fbf' }}>{row.obsidian}</td>
                  <td style={{ ...td, color: '#1677ff', fontWeight: 500 }}>{row.brain}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    })()}

    {/* Graph modals */}
    <Modal
      open={graphModal === 'obsidian'}
      onCancel={() => setGraphModal(null)}
      footer={null}
      width={540}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Tag color="purple" style={{ fontWeight: 700, fontSize: 11, margin: 0 }}>Obsidian</Tag>
          <Text style={{ fontSize: 15, fontWeight: 700, color: '#141414' }}>El Grafo de Obsidian</Text>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
        <img
          src="https://forum.obsidian.md/uploads/default/optimized/1X/a7fefb722b92c129d277510f83623169ceaac83b_2_690x459.jpeg"
          alt="Obsidian graph view"
          style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 200 }}
        />
        {[
          { label: 'Los nodos son archivos de texto', body: 'Cada nodo representa un documento Markdown. No hay atributos ni propiedades — el nodo es el archivo, y su "contenido" es el texto que tú has escrito.' },
          { label: 'Las conexiones las defines tú', body: 'Un enlace existe porque un humano escribió [[otro documento]] dentro del texto. No tienen significado semántico: solo indican que ese documento menciona o referencia a otro.' },
          { label: 'Uso: navegación intuitiva', body: 'El grafo sirve para que el autor explore visualmente cómo se relacionan sus ideas — "¿de qué temas hablo cuando hablo de este tema?". Es una herramienta de exploración personal, no una fuente de datos consultable.' },
        ].map((item, i) => (
          <div key={i} style={{ background: '#f9f0ff', borderRadius: 8, padding: '12px 16px', borderLeft: '3px solid #6c3fbf' }}>
            <Text strong style={{ display: 'block', fontSize: 13, color: '#22075e', marginBottom: 4 }}>{item.label}</Text>
            <Text style={{ fontSize: 12.5, color: '#595959', lineHeight: 1.65 }}>{item.body}</Text>
          </div>
        ))}
      </div>
    </Modal>

    <Modal
      open={graphModal === 'neo4j'}
      onCancel={() => setGraphModal(null)}
      footer={null}
      width={580}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Tag color="blue" style={{ fontWeight: 700, fontSize: 11, margin: 0 }}>Neo4j · Knowledge Graph</Tag>
          <Text style={{ fontSize: 15, fontWeight: 700, color: '#141414' }}>El Grafo de Cubicup Brain</Text>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
        <img
          src="https://dist.neo4j.com/wp-content/uploads/20230322152841/1wuLzLiIuJV-pyZUnccuNjA.png"
          alt="Knowledge graph — Neo4j"
          style={{ width: '100%', borderRadius: 8, objectFit: 'contain', maxHeight: 200, background: '#f0f7ff', border: '1px solid #91caff' }}
        />
        {[
          { label: 'Es una base de datos de propiedades', body: 'A diferencia del grafo de Obsidian, aquí cada nodo tiene atributos. Por ejemplo, el nodo Cliente puede tener Nombre, ID, Fecha de Registro — igual que una fila en una base de datos relacional, pero modelada como un grafo.' },
          { label: 'Los nodos los puede crear un LLM', body: 'Podemos dejar a un agente de IA que lea una reunión o un email y cree automáticamente nodos con sus atributos. La memoria del Brain crece sin intervención humana.' },
          { label: 'Las conexiones son relaciones etiquetadas', body: 'Las aristas tienen nombre: TRABAJA_EN, CASADO_CON, ES_HIJO_DE. Esto les da significado semántico real — la IA entiende qué tipo de relación existe entre dos nodos, no solo que "están conectados".' },
          { label: 'Se usa para consultas lógicas complejas', body: '¿Qué clientes TRABAJAN_EN el mismo sector que este lead y tienen un PRESUPUESTO mayor a 50K? ¿Cuáles son las incidencias o peticiones que han hecho una vez iniciada la obra? Preguntas que combinarían múltiples JOINs en SQL se resuelven de forma natural en un grafo de conocimiento.' },
        ].map((item, i) => (
          <div key={i} style={{ background: '#e6f4ff', borderRadius: 8, padding: '12px 16px', borderLeft: '3px solid #1677ff' }}>
            <Text strong style={{ display: 'block', fontSize: 13, color: '#003a8c', marginBottom: 4 }}>{item.label}</Text>
            <Text style={{ fontSize: 12.5, color: '#595959', lineHeight: 1.65 }}>{item.body}</Text>
          </div>
        ))}
      </div>
    </Modal>

  </div>
  );
};

// ── Slide 3: Obsidian vs Brain — comparativa pros/cons ───────────────────────

const Slide3: React.FC = () => {
  const prosRows = Math.max(obsidianPros.length, brainPros.length);
  const consRows = Math.max(obsidianCons.length, brainCons.length);

  const thBase: React.CSSProperties = { padding: '10px 20px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, borderBottom: '2px solid #e8e8e8', textAlign: 'left' };
  const td: React.CSSProperties = { padding: '9px 20px', fontSize: 12, lineHeight: 1.55, borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' };
  const sectionTd: React.CSSProperties = { padding: '6px 20px', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', borderBottom: '1px solid #e8e8e8' };

  return (
  <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 48px', gap: 20 }}>

    <div style={{ textAlign: 'center', flexShrink: 0 }}>
      <Text style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 4 }}>
        Obsidian vs Cubicup Brain
      </Text>
      <Title level={3} style={{ margin: 0, fontSize: 20 }}>Puntos fuertes y limitaciones</Title>
    </div>

    <div style={{ width: '100%', maxWidth: 920, flexShrink: 0, borderRadius: 12, overflow: 'hidden', border: '1px solid #e8e8e8', boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr style={{ background: '#fafafa' }}>
            <th style={{ ...thBase, width: '50%', color: '#6c3fbf', borderRight: '1px solid #efdbff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: 6, background: '#6c3fbf', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BulbOutlined style={{ color: '#fff', fontSize: 12 }} />
                </div>
                Obsidian · Herramienta para humanos
              </div>
            </th>
            <th style={{ ...thBase, width: '50%', color: '#1677ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: 6, background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BookOutlined style={{ color: '#fff', fontSize: 12 }} />
                </div>
                Cubicup Brain · Infraestructura para IAs
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Pros section header */}
          <tr style={{ background: '#f6ffed' }}>
            <td style={{ ...sectionTd, color: '#389e0d', borderRight: '1px solid #efdbff' }}>✓ Puntos fuertes</td>
            <td style={{ ...sectionTd, color: '#389e0d' }}>✓ Puntos fuertes</td>
          </tr>
          {Array.from({ length: prosRows }).map((_, i) => (
            <tr key={`pro-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ ...td, color: obsidianPros[i] ? '#22075e' : 'transparent', borderRight: '1px solid #efdbff' }}>
                {obsidianPros[i] && <><span style={{ color: '#52c41a', marginRight: 8 }}>✓</span>{obsidianPros[i]}</>}
              </td>
              <td style={{ ...td, color: brainPros[i] ? '#003a8c' : 'transparent' }}>
                {brainPros[i] && <><span style={{ color: '#52c41a', marginRight: 8 }}>✓</span>{brainPros[i]}</>}
              </td>
            </tr>
          ))}
          {/* Cons section header */}
          <tr style={{ background: '#fff2f0' }}>
            <td style={{ ...sectionTd, color: '#cf1322', borderRight: '1px solid #efdbff' }}>✗ Limitaciones</td>
            <td style={{ ...sectionTd, color: '#cf1322' }}>✗ Limitaciones actuales</td>
          </tr>
          {Array.from({ length: consRows }).map((_, i) => (
            <tr key={`con-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ ...td, color: obsidianCons[i] ? '#595959' : 'transparent', borderRight: '1px solid #efdbff' }}>
                {obsidianCons[i] && <><span style={{ color: '#ff4d4f', marginRight: 8 }}>✗</span>{obsidianCons[i]}</>}
              </td>
              <td style={{ ...td, color: brainCons[i] ? '#595959' : 'transparent' }}>
                {brainCons[i] && <><span style={{ color: '#ff4d4f', marginRight: 8 }}>✗</span>{brainCons[i]}</>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Card
      style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 10, width: '100%', maxWidth: 920, flexShrink: 0 }}
      styles={{ body: { padding: '12px 20px' } }}
    >
      <Space align="center" size={8}>
        <WarningOutlined style={{ color: '#fa8c16', fontSize: 14 }} />
        <Text style={{ fontSize: 13, color: '#614700' }}>
          <strong>Conclusión:</strong> Obsidian es ideal para uso personal. Cubicup Brain es para equipos que quieren que la IA lea, escriba y mejore el conocimiento — sin trabajo manual adicional.
        </Text>
      </Space>
    </Card>
  </div>
  );
};

// ── Slide 4: Roadmap ─────────────────────────────────────────────────────────

type Effort = 'done' | 'wip' | 'low' | 'medium' | 'high';

interface RoadmapItem { title: string; time?: string; useCase: string }

interface RoadmapCol {
  effort: Effort;
  label: string;
  sublabel: string;
  color: string;
  bg: string;
  border: string;
  icon: React.ReactNode;
  items: RoadmapItem[];
}

const ROADMAP_COLS: RoadmapCol[] = [
  {
    effort: 'done',
    label: 'Implementado',
    sublabel: 'En producción hoy',
    color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f',
    icon: <CheckCircleFilled />,
    items: [
      { title: 'Editor Markdown web (Brain)', useCase: 'El equipo de ventas documenta el proceso de una obra directamente en el browser. Los agentes lo leen como contexto en tiempo real sin ninguna exportación manual.' },
      { title: 'Historial y auditoría del Brain', useCase: 'Cada cambio en el Brain guarda una versión con autor y fecha, y queda en el registro de auditoría. Si algo se borra por error el contenido sigue en el historial.' },
      { title: 'Roles admin / editor / viewer', useCase: 'El equipo técnico edita fichas de obra, el comercial consulta perfiles de cliente y el cliente solo ve su expediente — todo desde el mismo Brain.' },
      { title: 'RAG en lead enrichment', useCase: 'Antes de contactar un lead, el agente busca en el Brain si ya hay historial previo, buyer persona del sector o notas de reuniones anteriores.' },
      { title: 'Análisis de reuniones', useCase: 'El agente transcribe la reunión, extrae dudas del cliente, acuerdos y próximos pasos, y los guarda estructurados en el Brain para que cualquier agente los consulte después.' },
      { title: 'Lead enrichment agent', useCase: 'Dado el nombre o empresa de un lead, el agente busca en LinkedIn y en el Brain, calcula un score de afinidad y devuelve un perfil completo listo para el comercial.' },
      { title: 'Marketplace de agentes', useCase: 'El equipo activa agentes pre-construidos (análisis de reunión, cualificación de lead, estimación de presupuesto) sin escribir una línea de código.' },
      { title: 'MCP Proxy para MongoDB Atlas', useCase: 'Los agentes consultan leads, presupuestos y proyectos en la base de datos de producción de forma segura (OAuth2, sin exponer credenciales). Ya permite consultas relacionales: "dime qué clientes están en el mismo sector que este lead, tienen presupuesto >50K y lista las incidencias que han tenido una vez iniciada la obra."' },
    ],
  },
  {
    effort: 'wip',
    label: 'En desarrollo',
    sublabel: 'Ahora mismo',
    color: '#c41d7f', bg: '#fff0f6', border: '#ffadd2',
    icon: <ThunderboltOutlined />,
    items: [
      { title: 'Estimador de presupuestos', time: '3–4 días', useCase: 'Tras la reunión con el cliente, el agente genera automáticamente un JSON de presupuesto estructurado (líneas de obra, IVA, total) usando los datos del lead y las notas de la reunión.' },
      { title: 'Brain como contexto global', time: '1–2 días', useCase: 'Cualquier agente (ventas, técnico, atención al cliente) puede preguntar al Brain sobre procedimientos internos, precios vigentes o el historial de un cliente — sin que nadie lo programe explícitamente cada vez.' },
      { title: 'Integración Monday.com en agentes', time: '2–3 días', useCase: 'Cuando el agente detecta un acuerdo en una reunión ("entregar planos el viernes"), crea automáticamente la tarea en el tablero de Monday del proyecto correspondiente.' },
    ],
  },
  {
    effort: 'low',
    label: 'Poco esfuerzo',
    sublabel: '1–5 días',
    color: '#1677ff', bg: '#e6f4ff', border: '#91caff',
    icon: <ThunderboltOutlined />,
    items: [
      { title: 'Agentes escriben al Brain', time: '1–2 días', useCase: 'Tras analizar una reunión, el agente actualiza directamente la ficha del cliente en el Brain — sin que nadie copie y pegue nada.' },
      { title: 'Search global en Brain', time: '1–2 días', useCase: '"¿Qué acordamos con el cliente García en la última reunión?" — el equipo busca en el Brain y obtiene el documento exacto en segundos.' },
      { title: 'RAG en cualquier agente', time: '1 día', useCase: 'El agente de presupuestos consulta los precios y procedimientos actualizados del Brain en lugar de tenerlos hardcodeados en el prompt.' },
      { title: 'Templates por departamento', time: '1 día', useCase: 'Plantilla de acta de reunión, ficha de lead, informe de visita de obra — el equipo rellena sobre una estructura estándar y los agentes saben dónde encontrar cada dato.' },
      { title: 'Notificaciones Slack desde agentes', time: '1 día', useCase: '"Nuevo lead cualificado: empresa Reformas García, presupuesto estimado 85K, alta afinidad con buyer persona Promotor." — directo al canal de ventas.' },
      { title: 'MCP para Claude Desktop', time: '2–3 días', useCase: 'El equipo usa Claude Desktop con acceso directo al Brain de Cubicup — el arquitecto pregunta a Claude sobre el expediente de un cliente y Claude lo consulta en tiempo real. Factible rápido: el Brain ya está en GitHub y la infraestructura MCP ya existe.' },
    ],
  },
  {
    effort: 'medium',
    label: 'Esfuerzo medio',
    sublabel: '1–3 semanas',
    color: '#d46b08', bg: '#fff7e6', border: '#ffd591',
    icon: <ClockCircleOutlined />,
    items: [
      { title: 'Búsqueda semántica (vectores)', time: '1 semana', useCase: '"Búscame proyectos similares a este — reforma integral en Barcelona, piso de 80m², cliente promotor." El agente encuentra los más parecidos aunque no coincidan las palabras exactas.' },
      { title: 'Presupuestos que aprenden', time: '1–2 semanas', useCase: 'Cada presupuesto aprobado se guarda en el Brain. Cuando se genera uno nuevo, el agente consulta los más similares para calibrar precios y evitar quedar por debajo o por encima del mercado real de Cubicup.' },
      { title: 'Memoria compartida entre agentes', time: '1 semana', useCase: 'El agente de reuniones anota que el cliente mencionó un presupuesto máximo de 60K. El agente de presupuestos lo lee automáticamente sin que nadie se lo diga.' },
      { title: 'Editor colaborativo real-time', time: '2–3 semanas', useCase: 'El arquitecto y el comercial editan la misma ficha de obra al mismo tiempo, ven los cambios del otro en vivo — como Google Docs pero dentro del Brain.' },
      { title: 'Playground como constructor de agentes', time: '2 semanas', useCase: '"Quiero un report semanal que lea los tableros de Monday y lo mande por email cada lunes a las 9h." — el Playground entiende la intención, configura el agente, conecta las herramientas (Monday reader + email sender) y programa el schedule. Sin tocar código ni formularios. Requiere: scheduler en el executor, tool de email, y que el Playground interprete intenciones de creación de agente.' },
    ],
  },
  {
    effort: 'high',
    label: 'Esfuerzo alto',
    sublabel: '+1 mes',
    color: '#531dab', bg: '#f9f0ff', border: '#d3adf7',
    icon: <RocketOutlined />,
    items: [
      { title: 'Grafo de conocimiento Neo4j', time: '4–6 semanas', useCase: 'Lo que MongoDB no puede hacer: recorridos multi-salto. "Dado este lead, encuéntrame todos los clientes conectados a él a través de su empresa, sector o arquitecto responsable — aunque no haya ningún campo directo que los una." O detección de patrones: "¿qué proyectos comparten proveedor Y arquitecto con uno que tuvo incidencia grave?" Esto requiere traversal nativo de grafo, no queries de documentos.' },
      { title: 'Analytics de uso del Brain', time: '2–3 semanas', useCase: 'Dashboard que muestra qué documentos leen más los agentes, qué secciones están desactualizadas y qué preguntas hacen los agentes que el Brain no sabe responder.' },
      { title: 'IA que sugiere qué documentar', time: '2–3 semanas', useCase: 'Tras 10 reuniones con un cliente sin ficha técnica actualizada: "Detectamos que el agente no encuentra información sobre los materiales preferidos de este cliente — ¿quieres que cree el documento?"' },
      { title: 'Onboarding automático via Brain', time: '1–2 semanas', useCase: 'Un nuevo empleado pregunta al Brain "¿cómo gestionamos las visitas de obra?" y obtiene el procedimiento actualizado, ejemplos reales y contactos relevantes — sin molestar a nadie del equipo.' },
    ],
  },
];

const Slide4: React.FC = () => {
  const [selected, setSelected] = useState<{ item: RoadmapItem; col: RoadmapCol } | null>(null);

  return (
  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '20px 32px', gap: 14, overflow: 'hidden' }}>

    <Title level={2} style={{ margin: 0, fontSize: 22, flexShrink: 0 }}>Roadmap</Title>

    <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 10 }}>
      {ROADMAP_COLS.map((col) => (
        <div key={col.effort} style={{
          flex: 1, borderRadius: 12, border: `1px solid ${col.border}`,
          background: col.bg, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0,
        }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ color: col.color, fontSize: 13 }}>{col.icon}</span>
              <Text strong style={{ color: col.color, fontSize: 12.5 }}>{col.label}</Text>
            </div>
            <Text style={{ fontSize: 10, color: col.color, opacity: 0.8 }}>{col.sublabel}</Text>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, overflowY: 'auto' }}>
            {col.items.map((item, i) => (
              <div
                key={i}
                onClick={() => setSelected({ item, col })}
                style={{
                  background: '#fff', borderRadius: 6, padding: '6px 10px',
                  border: `1px solid rgba(0,0,0,0.05)`, cursor: 'pointer',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = col.border; (e.currentTarget as HTMLDivElement).style.boxShadow = `0 2px 8px rgba(0,0,0,0.08)`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(0,0,0,0.05)'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
              >
                <Text style={{ fontSize: 11, color: '#262626', lineHeight: 1.4, display: 'block' }}>{item.title}</Text>
                {item.time && (
                  <Text style={{ fontSize: 9.5, color: col.color, opacity: 0.85, marginTop: 2, display: 'block' }}>
                    ⏱ {item.time}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <Modal
      open={!!selected}
      onCancel={() => setSelected(null)}
      footer={null}
      width={520}
      title={
        selected && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: selected.col.color, fontSize: 16 }}>{selected.col.icon}</span>
            <div>
              <Text style={{ fontSize: 11, color: selected.col.color, fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: 0.4 }}>{selected.col.label}</Text>
              <Text style={{ fontSize: 15, fontWeight: 700, color: '#141414' }}>{selected.item.title}</Text>
            </div>
          </div>
        )
      }
    >
      {selected && (
        <div style={{ paddingTop: 4 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 10 }}>
            Caso de uso en Cubicup
          </Text>
          <div style={{ background: selected.col.bg, borderRadius: 10, padding: '16px 20px', borderLeft: `3px solid ${selected.col.border}` }}>
            <Text style={{ fontSize: 13.5, color: '#262626', lineHeight: 1.75 }}>{selected.item.useCase}</Text>
          </div>
        </div>
      )}
    </Modal>

  </div>
  );
};

// ── Presentation shell ────────────────────────────────────────────────────────

const SLIDE_LABELS = [
  '¿Qué es Cubicup Brain?',
  'Obsidian vs Brain — definiciones',
  'Obsidian vs Brain — comparativa',
  'Roadmap',
  'Business Case',
];

const RoadmapPage: React.FC = () => {
  const [slide, setSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const total = 5;

  const goNext = useCallback(() => setSlide(s => Math.min(s + 1, total - 1)), []);
  const goPrev = useCallback(() => setSlide(s => Math.max(s - 1, 0)), []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext, goPrev, toggleFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  return (
    <Layout style={{ height: '100vh', background: 'transparent' }}>

      <Content style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'transparent' }}>

        {/* Slide area */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', background: '#fff', margin: '12px 24px 0', borderRadius: '12px 12px 0 0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {/* Slides */}
          {[<Slide1 />, <Slide2 />, <Slide3 />, <Slide4 />, <BusinessCaseContent />].map((content, i) => (
            <div
              key={i}
              style={{
                position: 'absolute', inset: 0,
                opacity: slide === i ? 1 : 0,
                pointerEvents: slide === i ? 'auto' : 'none',
                transition: 'opacity 0.3s ease',
              }}
            >
              {content}
            </div>
          ))}

          {/* Arrow buttons */}
          {slide > 0 && (
            <button
              onClick={goPrev}
              style={{
                position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                width: 40, height: 40, borderRadius: '50%', border: '1px solid #e8e8e8',
                background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10,
              }}
            >
              <LeftOutlined style={{ fontSize: 14, color: '#595959' }} />
            </button>
          )}
          {slide < total - 1 && (
            <button
              onClick={goNext}
              style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                width: 40, height: 40, borderRadius: '50%', border: '1px solid #e8e8e8',
                background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10,
              }}
            >
              <RightOutlined style={{ fontSize: 14, color: '#595959' }} />
            </button>
          )}
        </div>

        {/* Nav bar */}
        <div style={{
          height: 52, background: '#fff', margin: '0 24px 12px', borderRadius: '0 0 12px 12px',
          borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          <Text style={{ fontSize: 12, color: '#8c8c8c', minWidth: 120 }}>
            {slide + 1} / {total} — <strong style={{ color: '#262626' }}>{SLIDE_LABELS[slide]}</strong>
          </Text>

          <Space size={8}>
            {Array.from({ length: total }).map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                style={{
                  width: slide === i ? 24 : 8,
                  height: 8, borderRadius: 4,
                  border: 'none', cursor: 'pointer',
                  background: slide === i ? '#1677ff' : '#d9d9d9',
                  transition: 'all 0.25s ease',
                  padding: 0,
                }}
              />
            ))}
          </Space>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 120, justifyContent: 'flex-end' }}>
            <Text style={{ fontSize: 11, color: '#bfbfbf' }}>← → · F</Text>
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Salir de pantalla completa (F)' : 'Pantalla completa (F)'}
              style={{
                width: 30, height: 30, borderRadius: 6, border: '1px solid #e8e8e8',
                background: '#fafafa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {isFullscreen
                ? <FullscreenExitOutlined style={{ fontSize: 13, color: '#595959' }} />
                : <FullscreenOutlined style={{ fontSize: 13, color: '#595959' }} />}
            </button>
          </div>
        </div>

      </Content>
    </Layout>
  );
};

export default RoadmapPage;
